/**
 * Grabs one frame of the reporter's screen through the browser's share picker.
 *
 * Real pixels rather than a DOM re-render: html-to-image redraws the page from
 * its markup and misses exactly what bugs tend to live in — canvas, iframes,
 * cross-origin images, half-finished animations. The cost is the picker, which
 * we open before the dialog so the frame shows the page, not our modal.
 *
 * Resolves null when the reporter declines or the browser cannot share; a
 * report without a screenshot is still a report.
 */ export async function captureScreen() {
    if (!navigator.mediaDevices?.getDisplayMedia) return null;
    let stream;
    try {
        stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: 'browser'
            },
            audio: false,
            // Chromium-only hints that put this tab first in the picker. Unknown
            // keys are ignored elsewhere.
            preferCurrentTab: true,
            selfBrowserSurface: 'include'
        });
    } catch  {
        return null;
    }
    try {
        const video = document.createElement('video');
        video.muted = true;
        video.srcObject = stream;
        await video.play();
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        const blob = await new Promise((resolve)=>canvas.toBlob(resolve, 'image/png'));
        return blob ? new File([
            blob
        ], 'screenshot.png', {
            type: 'image/png'
        }) : null;
    } catch  {
        return null;
    } finally{
        // Stop immediately: we want one frame, and a lingering "sharing this tab"
        // banner would read as us still watching.
        stream.getTracks().forEach((track)=>track.stop());
    }
}
